import type { Article, ArticlesIndex, Category, Child } from '../types/articles';
import articlesJson from './articles.json';

export const articlesIndex = articlesJson as ArticlesIndex;

export const site = articlesIndex.site;

export function getCategory(key: string): Category {
  const category = articlesIndex.categories.find((c) => c.key === key);
  if (!category) {
    throw new Error(`unknown category: ${key}`);
  }
  return category;
}

export interface ChildHit {
  category: Category;
  child: Child;
}

/** 子要素 key から所属カテゴリごと引く (key はカテゴリ横断で一意)。 */
export function findChild(childKey: string): ChildHit | undefined {
  for (const category of articlesIndex.categories) {
    const child = category.children.find((c) => c.key === childKey);
    if (child) {
      return { category, child };
    }
  }
  return undefined;
}

export interface ArticleHit extends ChildHit {
  article: Article;
}

/** 記事 URL (/<section>/<slug>) から記事と所属する子要素・カテゴリを引く。 */
export function findArticle(section: string, slug: string): ArticleHit | undefined {
  for (const category of articlesIndex.categories) {
    for (const child of category.children) {
      const article = child.articles.find((a) => a.section === section && a.slug === slug);
      if (article) {
        return { category, child, article };
      }
    }
  }
  return undefined;
}

export function getSolutionWorks() {
  return articlesIndex.solutions;
}

export function findWork(workKey: string) {
  return articlesIndex.solutions.find((w) => w.key === workKey);
}

/**
 * /solutions/:work/<rest> に対応する問題を引く。巻構成の作品は rest の
 * 先頭セグメントが巻キー。無ければ undefined。
 */
export function findSolutionProblem(workKey: string, rest: string) {
  const work = findWork(workKey);
  if (!work) {
    return undefined;
  }
  if (work.books.length === 0) {
    const problem = work.problems.find((p) => p.path === rest);
    return problem ? { work, book: null, problems: work.problems, problem } : undefined;
  }
  const [bookKey, ...restSegs] = rest.split('/');
  const book = work.books.find((b) => b.key === bookKey);
  const path = restSegs.join('/');
  const problem = book?.problems.find((p) => p.path === path);
  return book && problem ? { work, book, problems: book.problems, problem } : undefined;
}

/** 問題ページの URL (/solutions/...)。 */
export function problemUrl(workKey: string, bookKey: string | null, path: string): string {
  return bookKey ? `/solutions/${workKey}/${bookKey}/${path}` : `/solutions/${workKey}/${path}`;
}
